/**
 * Distribution Config Editor
 * Renders the appropriate config form based on distribution type
 */

import { useState, useEffect } from 'react'
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  IconButton,
  Chip,
  Stack,
} from '@mui/material'
import { Add, Close } from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { format, parseISO } from 'date-fns'
import {
  DistributionType,
  type IntervalUnit,
  type TimeIntervalConfig,
  type SpecificDatesConfig,
  type EveryNthConfig,
  type PercentageConfig,
  type DistributionConfig,
} from '@api/types/rewards'

interface DistributionConfigEditorProps {
  distributionType: DistributionType
  config: DistributionConfig
  onChange: (config: DistributionConfig) => void
}

const INTERVAL_UNITS: { value: IntervalUnit; label: string }[] = [
  { value: 'hour', label: 'Hour(s)' },
  { value: 'day', label: 'Day(s)' },
  { value: 'week', label: 'Week(s)' },
  { value: 'month', label: 'Month(s)' },
  { value: 'year', label: 'Year(s)' },
]

export default function DistributionConfigEditor({
  distributionType,
  config,
  onChange,
}: DistributionConfigEditorProps) {
  // TimeInterval state
  const [intervalAmount, setIntervalAmount] = useState(1)
  const [intervalUnit, setIntervalUnit] = useState<IntervalUnit>('day')
  const [quantityPerInterval, setQuantityPerInterval] = useState(1)

  // SpecificDates state
  const [dates, setDates] = useState<string[]>([])
  const [quantityPerDate, setQuantityPerDate] = useState(1)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)

  // EveryNth state
  const [nthValue, setNthValue] = useState(10)

  // Percentage state
  const [percentage, setPercentage] = useState(25)

  // Initialize from existing config
  useEffect(() => {
    if (!config) return

    if ('intervalAmount' in config) {
      const c = config as TimeIntervalConfig
      setIntervalAmount(c.intervalAmount)
      setIntervalUnit(c.intervalUnit)
      setQuantityPerInterval(c.quantityPerInterval)
    } else if ('dates' in config) {
      const c = config as SpecificDatesConfig
      setDates(c.dates)
      setQuantityPerDate(c.quantityPerDate)
    } else if ('nthValue' in config) {
      const c = config as EveryNthConfig
      setNthValue(c.nthValue)
    } else if ('percentage' in config) {
      const c = config as PercentageConfig
      setPercentage(c.percentage)
    }
  }, [])

  // Update parent when values change
  useEffect(() => {
    let newConfig: DistributionConfig = null

    switch (distributionType) {
      case DistributionType.TimeInterval:
        newConfig = { intervalAmount, intervalUnit, quantityPerInterval }
        break
      case DistributionType.SpecificDates:
        newConfig = { dates, quantityPerDate }
        break
      case DistributionType.EveryNth:
        newConfig = { nthValue }
        break
      case DistributionType.Percentage:
        newConfig = { percentage }
        break
      case DistributionType.Manual:
        newConfig = null
        break
    }

    onChange(newConfig)
  }, [
    distributionType,
    intervalAmount,
    intervalUnit,
    quantityPerInterval,
    dates,
    quantityPerDate,
    nthValue,
    percentage,
  ])

  const handleAddDate = () => {
    if (selectedDate) {
      const dateStr = format(selectedDate, 'yyyy-MM-dd')
      if (!dates.includes(dateStr)) {
        setDates([...dates, dateStr].sort())
      }
      setSelectedDate(null)
    }
  }

  const handleRemoveDate = (dateToRemove: string) => {
    setDates(dates.filter((d) => d !== dateToRemove))
  }

  // Render based on distribution type
  switch (distributionType) {
    case DistributionType.Manual:
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          Manual distribution - rewards will be distributed via the group management interface or
          API.
        </Alert>
      )

    case DistributionType.TimeInterval:
      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Distribute rewards automatically on a schedule
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField
              label="Every"
              type="number"
              value={intervalAmount}
              onChange={(e) => setIntervalAmount(Math.max(1, parseInt(e.target.value) || 1))}
              inputProps={{ min: 1 }}
              sx={{ width: 100 }}
            />
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Unit</InputLabel>
              <Select
                value={intervalUnit}
                label="Unit"
                onChange={(e) => setIntervalUnit(e.target.value as IntervalUnit)}
              >
                {INTERVAL_UNITS.map((unit) => (
                  <MenuItem key={unit.value} value={unit.value}>
                    {unit.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Quantity"
              type="number"
              value={quantityPerInterval}
              onChange={(e) => setQuantityPerInterval(Math.max(1, parseInt(e.target.value) || 1))}
              inputProps={{ min: 1 }}
              helperText="per interval"
              sx={{ width: 120 }}
            />
          </Box>
        </Box>
      )

    case DistributionType.SpecificDates:
      return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Distribute rewards on specific dates
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'flex-start' }}>
              <DatePicker
                label="Add Date"
                value={selectedDate}
                onChange={(date) => setSelectedDate(date)}
                minDate={new Date()}
                slotProps={{
                  textField: { size: 'small' },
                }}
              />
              <IconButton
                color="primary"
                onClick={handleAddDate}
                disabled={!selectedDate}
                sx={{ mt: 0.5 }}
              >
                <Add />
              </IconButton>
              <TextField
                label="Quantity per date"
                type="number"
                value={quantityPerDate}
                onChange={(e) => setQuantityPerDate(Math.max(1, parseInt(e.target.value) || 1))}
                inputProps={{ min: 1 }}
                size="small"
                sx={{ width: 140 }}
              />
            </Box>

            {dates.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2, gap: 1 }}>
                {dates.map((date) => (
                  <Chip
                    key={date}
                    label={format(parseISO(date), 'MMM d, yyyy')}
                    onDelete={() => handleRemoveDate(date)}
                    deleteIcon={<Close />}
                    variant="outlined"
                  />
                ))}
              </Stack>
            )}

            {dates.length === 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Add at least one date for distribution
              </Alert>
            )}
          </Box>
        </LocalizationProvider>
      )

    case DistributionType.EveryNth:
      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Distribute a reward for every Nth qualifying action
          </Typography>
          <TextField
            label="Every Nth"
            type="number"
            value={nthValue}
            onChange={(e) => setNthValue(Math.max(1, parseInt(e.target.value) || 1))}
            inputProps={{ min: 1 }}
            helperText={`Every ${nthValue}${getOrdinalSuffix(nthValue)} completion wins a reward`}
            sx={{ mt: 2, width: 150 }}
          />
        </Box>
      )

    case DistributionType.Percentage:
      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Randomly distribute rewards to a percentage of users
          </Typography>
          <TextField
            label="Win Percentage"
            type="number"
            value={percentage}
            onChange={(e) =>
              setPercentage(Math.min(100, Math.max(1, parseInt(e.target.value) || 1)))
            }
            inputProps={{ min: 1, max: 100 }}
            helperText={`${percentage}% chance to win on each qualifying action`}
            sx={{ mt: 2, width: 150 }}
            InputProps={{
              endAdornment: <Typography color="text.secondary">%</Typography>,
            }}
          />
        </Box>
      )

    default:
      return null
  }
}

function getOrdinalSuffix(n: number): string {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return s[(v - 20) % 10] || s[v] || s[0]
}
